import React, { useState, useEffect, useMemo } from 'react'
import { Form, message, Input, Checkbox } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons'
import { Link, navigate } from 'gatsby'
import { auth0Roles, strapiURL } from '../../../config'
import cookie from 'react-cookies'
import axios from 'axios'
import { formatPrice } from '../../../functions'
import GlobalData from '../../../../globalData.json'
import { useDispatch } from 'react-redux'
import { setProfileData, setProducts } from '../../../redux/actions/user'
import useGMT from '../../../hooks/useGTM'
import { TESTIMONIAL_SIGNUP } from '../../../data'

const SignUpForm = ({ inModal, closeHandler, form }) => {
  const [listingPrices, setListingPrices] = useState([])
  const [productLoading, setProductLoading] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const dispatch = useDispatch()
  const {
    pushRegisterEvent,
    pushSubscribeToNewsletterEvent,
    pushCandidateSelectItemEvent,
    pushCandidateAddToCartEvent,
    pushCandidateCheckoutEvent,
  } = useGMT()

  useEffect(() => {
    setProductLoading(true)
    let _prices = GlobalData?.CandidateCatalog?.prices?.length
      ? GlobalData?.CandidateCatalog?.prices?.filter(
          item => !item.isDiscontinued
        )
      : []
    setListingPrices(_prices)
    setProductLoading(false)
  }, [])

  useEffect(() => {
    if (subscription) {
      pushCandidateSelectItemEvent(subscription)
    }
  }, [subscription])

  const signUp = async values => {
    let preCheckData = {
      email: (values.email || '').toLowerCase(),
    }

    const result = await axios.post(
      strapiURL + '/auth0/checkEmail',
      preCheckData
    )

    const resultEmail = result.data

    if (resultEmail.format === false) {
      message.error('Email format is invalid.')
      return
    }

    if (resultEmail.mxLookup === false) {
      message.error('Cannot verify email domain')
      return
    }

    if (resultEmail.smtp === false) {
      message.error('Cannot validate email smtp setting')
      return
    }

    const regex =
      /^((?=.*[A-Z])(?=.*[a-z])(?=.*\d)|(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])|(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])|(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*])).{8,16}$/
    if (values.email !== values.confirmEmail) {
      message.error("Email didn't match.")
      return
    }
    if (values.password !== values.confirmPassword) {
      message.error("Password didn't match.")
      return
    }
    if (
      values.confirmPassword.length < 8 ||
      !values.confirmPassword.match(regex)
    ) {
      message.error(
        'Your password must contain at least 8 characters including at least 3 of the following 4 types of characters: a lower-case letter, an upper-case letter, a number, a special character (such as !@#$%^&*).'
      )
      return
    }

    pushRegisterEvent(values?.is_newsletter_subscriber || false)
    pushCandidateAddToCartEvent(subscription)
    pushCandidateCheckoutEvent(subscription)

    setSubmitLoading(true)
    let data = {
      email: (values.email || '').toLowerCase(),
      password: values.password,
      last_login: new Date(),
      roleId: auth0Roles.Member.id,
      firstName: values.firstName,
      lastName: values.lastName,
      name: values.firstName + ' ' + values.lastName,
      is_newsletter_subscriber: values?.is_newsletter_subscriber || false,
    }

    axios
      .post(strapiURL + '/auth0/signup', data)
      .then(res => {
        const userData = res.data
        const responseRole = userData.role.id
        let profile = userData?.profile
        delete userData.profile

        if (values?.is_newsletter_subscriber) {
          pushSubscribeToNewsletterEvent({
            contacttype: userData?.role?.name || '',
            loginstatus: 'loggedin',
          })
        }

        cookie.save('userRole', responseRole, { path: '/', maxAge: 259200 })
        cookie.save('user', userData, { path: '/', maxAge: 259200 })

        dispatch(setProfileData(profile))

        if (!subscription && typeof window !== 'undefined') {
          window.location.href = '/member-dashboard'
        } else if (!userData.isCheckoutRequired) {
          alert('You already have a subscription with us.')

          // Retrieve updated subscription
          axios
            .get(
              strapiURL +
                `/employers/${userData?.user?.id}/subscriptionsAndCredits`
            )
            .then(res => {
              dispatch(setProducts(res?.data))
              navigate('/member-dashboard/', {
                state: { firstRegistration: true },
              })
            })
            .catch(err => {
              console.log(
                'Something wrong while retrive subscriptionsAndCredits.'
              )
            })
        } else {
          if (typeof window !== 'undefined') {
            let _checkoutPrices = []
            _checkoutPrices.push({
              price: subscription,
              quantity: 1,
            })
            localStorage.setItem('can-subscription', subscription)

            axios
              .post(strapiURL + '/stripe/session/checkout', {
                line_items: _checkoutPrices,
                success_url:
                  window.location.origin +
                  '/thankyou/?success=true&session_id={CHECKOUT_SESSION_ID}&first_registration=true',
                cancel_url: window.location.origin,
                customer: userData.customer_id,
                customerEmail: (values.email || '').toLowerCase(),
                mode: 'subscription',
                clientId: cookie.load('_ga')?.substring(6),
                sessionId: cookie
                  .load(
                    process.env.GATSBY_GOOGLE_ANALYTICS_MEASUREMENT_ID?.replace(
                      'G-',
                      '_ga_'
                    )
                  )
                  ?.split('.')[2],
              })
              .then(res => {
                if (typeof window !== 'undefined') {
                  window.history.replaceState(
                    '',
                    '',
                    `${window.location.origin}/thankyou/`
                  )
                  window.location.href = res?.data?.url
                }
              })
              .catch(err => {
                message.error('Something wrong while checkout.')
              })
          }
        }
      })
      .catch(err => {
        message.error(err?.response?.data?.message[0].error)
        setSubmitLoading(false)
      })
  }

  const testimonialData = useMemo(() => {
    const index = Math.floor(Math.random() * TESTIMONIAL_SIGNUP.length)
    return TESTIMONIAL_SIGNUP[index]
  }, [])

  return (
    <div className="bg-white w-full flex rounded ">
      <div className="w-full md:w-3/5 py-5 lg:py-8">
        <div className="px-[30px] lg:px-12 mb-5">
          <img
            className="h-4.5 lg:h-10"
            src="/images/logo.png"
            alt="Medreps logo"
          />
        </div>
        <div className="bg-voyage md:hidden py-8">
          <div className="flex flex-col items-center px-[20px]">
            <img src="/images/login-modal-image.png" className="w-1/2" />
            <span className="font-bold text-white text-center mt-2 leading-tight">
              90% of members would recommend MedReps
            </span>
            <span className="font-light text-white text-center leading-tight">
              to a friend looking for a medical sales job
            </span>
          </div>
        </div>
        <div className="px-[30px] lg:px-12 pt-4 lg:pt-8">
          <p className="font-playfair text-headingFour lg:text-headingThree font-normal leading-tight mb-0.5">
            Register for a MedReps membership
          </p>
          <p className="text-lg font-sans mb-2">
            Get unlimited access to medical sales jobs, career advice and salary
            information
          </p>
          {productLoading ? (
            <div
              className="w-full md:w-9/12 h-52 bg-no-repeat bg-cover"
              style={{
                backgroundImage: `url(/loaders/products-loader-mobile.png)`,
              }}
            />
          ) : (
            <div className="options-div w-full md:w-9/12">
              {listingPrices?.map((price, priceIndex) => {
                let isSelected = subscription === price?.price?.priceId
                return (
                  <div
                    onClick={() => {
                      if (subscription === price?.price?.priceId) {
                        setSubscription(null)
                        return
                      }
                      setSubscription(price?.price?.priceId)
                    }}
                    key={priceIndex}
                    className={`mb-3 single-option bg-white shadow-md flex justify-between items-center p-4 rounded-md cursor-pointer border-2 border-transparent ${
                      isSelected
                        ? 'border-2 border-merlot active-card-shadow'
                        : ''
                    }`}
                  >
                    <div className="flex items-center">
                      {isSelected ? (
                        <div className="flex items-center justify-center active bg-merlot h-6 md:h-9 w-6 md:w-9 bg-merlot rounded-full flex items-center justify-center">
                          <img src="/icons/check-mark.svg" className="mb-0" />
                        </div>
                      ) : (
                        <div className="inactive bg-border h-6 md:h-9 w-6 md:w-9 bg-merlot rounded-full flex items-center justify-center" />
                      )}

                      <div className="ml-4">
                        <p className="font-sans font-bold text-lg mb-0">
                          {price?.price?.name || price?.name} membership
                        </p>
                        <p className="font-sans text-xs mb-1 italic">
                          {price?.price?.red_desc}
                        </p>
                      </div>
                    </div>
                    <p className="font-sans font-bold text-lg mb-0">
                      {formatPrice(price?.price?.amount, 'USD')}
                    </p>
                  </div>
                )
              })}
            </div>
          )}

          <Form
            className="pt-6"
            form={form}
            layout="vertical"
            onFinish={signUp}
          >
            <div className="flex items-center justify-between flex-wrap">
              <div className="w-full md:w-48%">
                <Form.Item
                  name="firstName"
                  className="theme-form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input
                    name="firstName"
                    type="text"
                    placeholder="First Name"
                    className="placeholder-charcoal focus:border-merlot focus:outline-none focus:shadow-none"
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-48%">
                <Form.Item
                  name="lastName"
                  className="theme-form-item"
                  rules={[
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input
                    name="lastName"
                    type="text"
                    placeholder="Last Name"
                    className="placeholder-charcoal focus:border-merlot focus:outline-none focus:shadow-none"
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-48%">
                <Form.Item
                  name="email"
                  className="theme-form-item"
                  rules={[
                    {
                      type: 'email',
                      message: 'Invalid Email.',
                    },
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input
                    name="email"
                    type="email"
                    placeholder="E-Mail"
                    className="placeholder-charcoal focus:border-merlot focus:outline-none focus:shadow-none"
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-48%">
                <Form.Item
                  name="confirmEmail"
                  className="theme-form-item"
                  rules={[
                    {
                      type: 'email',
                      message: 'Invalid Email.',
                    },
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input
                    name="confirmEmail"
                    type="email"
                    placeholder="Confirm E-Mail"
                    className="placeholder-charcoal focus:border-merlot focus:outline-none focus:shadow-none"
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-48%">
                <Form.Item
                  className="theme-form-item-password"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input.Password
                    name="password"
                    placeholder="Password"
                    iconRender={visible =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-48%">
                <Form.Item
                  className="theme-form-item-password"
                  name="confirmPassword"
                  rules={[
                    {
                      required: true,
                      message: 'Required.',
                    },
                  ]}
                >
                  <Input.Password
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    iconRender={visible =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
              </div>
            </div>
            <Form.Item
              className="theme-form-item mb-1"
              name="remember"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            'Please review the terms and conditions and check the box to proceed.'
                          )
                        ),
                },
              ]}
            >
              <div className="flex items-start">
                <span className="mr-[4px] text-xl text-[#ff4d4f]">*</span>
                <Checkbox name="remember" className="font-semibold font-sans">
                  I have read and agree to the MedReps{' '}
                  <a href="/privacy-policy" target="_blank">
                    Terms & Conditions
                  </a>
                </Checkbox>
              </div>
            </Form.Item>
            <p className="pt-0 font-sans text-last mb-4">
              We think it's a good idea to stay plugged into the job market, so
              unless you request otherwise, your membership will automatically
              renew.
            </p>
            <p className="pt-0 font-sans text-last mb-6">
              By creating a MedReps account, you agree to receive marketing
              communications, updates, and relevant information about our
              services and offers. We are committed to respecting your
              preferences, and you can unsubscribe from these communications at
              any time by following the link provided in our emails or
              contacting our support team. Your privacy is important to us, and
              we ensure that your information is safe and handled in accordance
              with{' '}
              <a href="/privacy-policy" target="_blank">
                MedReps' privacy policy
              </a>
              .
            </p>

            <Form.Item
              className="theme-form-item mb-1"
              name="is_newsletter_subscriber"
              valuePropName="checked"
            >
              <Checkbox name="subscribe" className="font-semibold font-sans">
                Subscribe to The Brief, MedReps' newsletter featuring latest
                medical sales career and industry insights
              </Checkbox>
            </Form.Item>
            <p className="pt-0 font-sans text-last mb-6">
              By subscribing to our mailing list, you agree to receive marketing
              communications, updates, and relevant information about our
              services and offers. We are committed to respecting your
              preferences, and you can unsubscribe from these communications at
              any time by following the link provided in our emails or
              contacting our support team. Your privacy is important to us, and
              we ensure that your information is safe and handled in accordance
              with{' '}
              <a href="/privacy-policy" target="_blank">
                MedReps' privacy policy
              </a>
              .
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  form.submit()
                }}
                className="bg-voyage py-2 px-6 text-white font-sans rounded font-semibold text-sn"
              >
                {submitLoading ? 'Loading...' : 'Register'}
              </button>
              <p className="font-sans text-sm font-semibold mb-0 mt-4">
                Want to post jobs?
              </p>
              <p className="font-sans font-normal text-last mb-0">
                Employers/Recruiters&nbsp;
                <Link
                  to="/employer/signup"
                  className="text-poppy underline font-semibold text-last"
                >
                  Click Here
                </Link>{' '}
              </p>
            </div>
          </Form>
        </div>
      </div>
      <div className="w-2/5 bg-voyage rounded-tr rounded-br relative px-4 hidden md:block">
        {inModal ? (
          <img
            onClick={() => closeHandler && closeHandler()}
            className="absolute right-0 top-0 w-6 mr-8 mt-8 cursor-pointer"
            src="/icons/icon-modal-close.svg"
            alt="Close modal icon"
          />
        ) : null}
        <div className="flex pt-12 gap-4 mt-4">
          <img
            src="/images/candidate-login-image.png"
            alt="Login phone icon"
            className="w-1/4 h-fit"
          />
          <div>
            <p className="font-sans text-[#FFF8F8] text-lg font-bold">
              “{testimonialData?.message}”
            </p>
            <p className="font-sans text-[#FFF8F8] text-lg font-bold">
              - {testimonialData?.by}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignUpForm
